import React, { useContext, useState } from 'react';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import CustomInput from '../inputs/customInput';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/authService';
import { AuthContext } from '../../provider/authProvider';
import { useNavigate } from 'react-router-dom';

function LoginForm() {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const [formValues, setFormValues] = useState({
        phone: '',
        password: '',
    });

    const [errors, setErrors] = useState({});
    const [loginError, setLoginError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formValues.phone) {
            newErrors.phone = t('phoneRequired');
        }

        if (!formValues.password) {
            newErrors.password = t('passwordRequired');
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await AuthService.login(formValues, i18n.language);
                login(data);
                setLoginError(null);

                const permissions = data.user?.permissions?.map(p => p.value) || [];

                const routeByPermission = {
                    dashboard: '/dashboard',
                    post: '/posts',
                    page: '/pages',
                    driver: '/drivers',
                    expense: '/expenses',
                    walletTransfer: '/wallet-transfer',
                    loan: '/loans',
                    report: '/profit-report',
                    notification: '/notifications',
                    whatsAppMsg: '/whatsapp-msgs',
                    user: '/users',
                    role: '/roles',
                    city: '/cities',
                    branch: '/branches',
                    company: '/company-info'
                };

                const firstAllowedRoute = Object.entries(routeByPermission).find(
                    ([perm]) => permissions.includes(perm)
                )?.[1] || '/profile';

                navigate(firstAllowedRoute);
            } catch (error) {
                setIsLoading(false);
                setLoginError(error.response.data.error);
            }
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
                width: '100%',
                maxWidth: 460,
                mx: 'auto',
                px: 6,
                py: 8,
                boxShadow: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
            }}
        >
            <Box sx={{ textAlign: 'center', color: theme.palette.text.primary, marginBottom: 4 }}>
                <p style={{ fontSize: 30, margin: 0 }}>{t('loginTitle')}</p>
                <p style={{ fontSize: 13, marginTop: 6, marginBottom: 20 }}>{t('loginDes')}</p>
            </Box>
            <CustomInput
                label={t('phone')}
                placeholder={t('phoneHere')}
                name="phone"
                type='text'
                value={formValues.phone}
                error={!!errors.phone}
                helperText={errors.phone}
                onChange={handleChange}
            />
            <CustomInput
                label={t('password')}
                placeholder="******"
                name="password"
                type="password"
                value={formValues.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
            />

            {loginError && (
                <Box sx={{ color: theme.palette.error.main, fontSize: 13, textAlign: 'center', mt: 2 }}>
                    {loginError}
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 200, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> : t('login')}
                </Button>
            </Box>
        </Box>
    );
}

export default LoginForm;
